/**
 * CreateContactModal — Contact-Vollausbau
 *
 * Sektionen: Stammdaten | Wallets | Zahlung | Optionen
 * Nur Name ist Pflichtfeld.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateContact } from './useContactMutations'
import type { ContactFormData } from './useContactMutations'
import { toast } from '../../components/Toast/useToast'

type Props = {
  isOpen: boolean
  onClose: () => void
}

type FormErrors = {
  name?: string
  email?: string
}

function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export function CreateContactModal({ isOpen, onClose }: Props) {
  const { t } = useTranslation('contacts')
  // Stammdaten
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [address, setAddress] = useState('')
  const [notes, setNotes] = useState('')

  // Wallets
  const [heliumWallet, setHeliumWallet] = useState('')
  const [btcWallet, setBtcWallet] = useState('')
  const [ethWallet, setEthWallet] = useState('')
  const [usdtWallet, setUsdtWallet] = useState('')
  const [usdcWallet, setUsdcWallet] = useState('')

  // Zahlung
  const [currency, setCurrency] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('')
  const [iban, setIban] = useState('')
  const [paypal, setPaypal] = useState('')

  // Optionen
  const [monthlyReport, setMonthlyReport] = useState(false)
  const [broadcastEnabled, setBroadcastEnabled] = useState(true)
  const [tagList, setTagList] = useState('')

  const [errors, setErrors] = useState<FormErrors>({})

  const createContact = useCreateContact()

  function validate(): boolean {
    const newErrors: FormErrors = {}

    if (!name.trim()) {
      newErrors.name = t('createModal.validation.nameRequired')
    }

    if (email && !validateEmail(email)) {
      newErrors.email = t('createModal.validation.emailInvalid')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return

    const data: ContactFormData = {
      name: name.trim(),
      email: email || null,
      phone: phone || null,
      address: address || null,
      notes: notes || null,
      helium_wallet: heliumWallet || null,
      btc_wallet: btcWallet || null,
      eth_wallet: ethWallet || null,
      usdt_wallet: usdtWallet || null,
      usdc_wallet: usdcWallet || null,
      currency: currency || null,
      payment_method: paymentMethod || null,
      iban: iban || null,
      paypal: paypal || null,
      monthly_report: monthlyReport,
      broadcast_enabled: broadcastEnabled,
      tag_list: tagList.trim() || undefined,
    }

    try {
      await createContact.mutateAsync(data)
      toast.success(t('createModal.toastSuccess'))
      handleClose()
    } catch {
      toast.error(t('createModal.toastError'))
    }
  }

  function handleClose() {
    setName('')
    setEmail('')
    setPhone('')
    setAddress('')
    setNotes('')
    setHeliumWallet('')
    setBtcWallet('')
    setEthWallet('')
    setUsdtWallet('')
    setUsdcWallet('')
    setCurrency('')
    setPaymentMethod('')
    setIban('')
    setPaypal('')
    setMonthlyReport(false)
    setBroadcastEnabled(true)
    setTagList('')
    setErrors({})
    onClose()
  }

  if (!isOpen) return null

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-contact-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose()
      }}
      data-testid="create-contact-modal"
      style={{ alignItems: 'flex-start', paddingTop: 32 }}
    >
      <div className="modal" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h2 id="create-contact-title">{t('createModal.title')}</h2>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            aria-label={t('createModal.closeAriaLabel')}
            onClick={handleClose}
          >
            &times;
          </button>
        </div>

        <form
          onSubmit={(e) => { void handleSubmit(e) }}
          style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}
        >
          <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>

            {/* --- Stammdaten --- */}
            <div className="form-section">
              <h3 className="form-section-title">{t('createModal.sections.masterdata')}</h3>

              <div className="form-group">
                <label htmlFor="contact-name">
                  {t('createModal.fields.name')} <span aria-hidden="true">*</span>
                </label>
                <input
                  id="contact-name"
                  className={`input${errors.name ? ' is-error' : ''}`}
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('createModal.placeholders.name')}
                  aria-required="true"
                  aria-describedby={errors.name ? 'name-error' : undefined}
                  data-testid="contact-name-input"
                />
                {errors.name && (
                  <span id="name-error" className="field-error" role="alert">
                    {errors.name}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="contact-email">{t('createModal.fields.email')}</label>
                <input
                  id="contact-email"
                  className={`input${errors.email ? ' is-error' : ''}`}
                  type="text"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="max@example.com"
                  aria-describedby={errors.email ? 'email-error' : undefined}
                  data-testid="contact-email-input"
                />
                {errors.email && (
                  <span id="email-error" className="field-error" role="alert">
                    {errors.email}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="contact-phone">{t('createModal.fields.phone')}</label>
                <input
                  id="contact-phone"
                  className="input"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t('createModal.placeholders.phone')}
                  data-testid="contact-phone-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="contact-address">{t('createModal.fields.address')}</label>
                <input
                  id="contact-address"
                  className="input"
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder={t('createModal.placeholders.address')}
                  data-testid="contact-address-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="contact-notes">{t('createModal.fields.notes')}</label>
                <textarea
                  id="contact-notes"
                  className="input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder={t('createModal.placeholders.notes')}
                  data-testid="contact-notes-input"
                />
              </div>
            </div>

            <hr className="form-divider" />

            {/* --- Wallets --- */}
            <div className="form-section">
              <h3 className="form-section-title">{t('createModal.sections.wallets')}</h3>

              <div className="form-group">
                <label htmlFor="contact-helium-wallet">{t('createModal.fields.heliumWallet')}</label>
                <input
                  id="contact-helium-wallet"
                  className="input"
                  type="text"
                  value={heliumWallet}
                  onChange={(e) => setHeliumWallet(e.target.value)}
                  placeholder={t('createModal.placeholders.heliumWallet')}
                  data-testid="contact-wallet-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="contact-btc-wallet">{t('createModal.fields.btcWallet')}</label>
                <input
                  id="contact-btc-wallet"
                  className="input"
                  type="text"
                  value={btcWallet}
                  onChange={(e) => setBtcWallet(e.target.value)}
                  placeholder={t('createModal.placeholders.btcWallet')}
                  data-testid="contact-btc-wallet-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="contact-eth-wallet">{t('createModal.fields.ethWallet')}</label>
                <input
                  id="contact-eth-wallet"
                  className="input"
                  type="text"
                  value={ethWallet}
                  onChange={(e) => setEthWallet(e.target.value)}
                  placeholder={t('createModal.placeholders.ethWallet')}
                  data-testid="contact-eth-wallet-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="contact-usdt-wallet">{t('createModal.fields.usdtWallet')}</label>
                <input
                  id="contact-usdt-wallet"
                  className="input"
                  type="text"
                  value={usdtWallet}
                  onChange={(e) => setUsdtWallet(e.target.value)}
                  data-testid="contact-usdt-wallet-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="contact-usdc-wallet">{t('createModal.fields.usdcWallet')}</label>
                <input
                  id="contact-usdc-wallet"
                  className="input"
                  type="text"
                  value={usdcWallet}
                  onChange={(e) => setUsdcWallet(e.target.value)}
                  data-testid="contact-usdc-wallet-input"
                />
              </div>
            </div>

            <hr className="form-divider" />

            {/* --- Zahlung --- */}
            <div className="form-section">
              <h3 className="form-section-title">{t('createModal.sections.payment')}</h3>

              <div className="form-group">
                <label htmlFor="contact-currency">{t('createModal.fields.currency')}</label>
                <select
                  id="contact-currency"
                  className="input"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  data-testid="contact-currency-select"
                >
                  <option value="">{t('createModal.currencyPlaceholder')}</option>
                  <option value="eur">EUR</option>
                  <option value="usd">USD</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="contact-payment-method">{t('createModal.fields.paymentMethod')}</label>
                <select
                  id="contact-payment-method"
                  className="input"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  data-testid="contact-payment-method-select"
                >
                  <option value="">{t('createModal.paymentMethodPlaceholder')}</option>
                  <option value="bank_transfer">{t('createModal.paymentMethods.bank_transfer')}</option>
                  <option value="paypal">{t('createModal.paymentMethods.paypal')}</option>
                  <option value="crypto">{t('createModal.paymentMethods.crypto')}</option>
                  <option value="cryptoiot">{t('createModal.paymentMethods.cryptoiot')}</option>
                  <option value="cash">{t('createModal.paymentMethods.cash')}</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="contact-iban">{t('createModal.fields.iban')}</label>
                <input
                  id="contact-iban"
                  className="input"
                  type="text"
                  value={iban}
                  onChange={(e) => setIban(e.target.value)}
                  placeholder={t('createModal.placeholders.iban')}
                  data-testid="contact-iban-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="contact-paypal">{t('createModal.fields.paypal')}</label>
                <input
                  id="contact-paypal"
                  className="input"
                  type="text"
                  inputMode="email"
                  value={paypal}
                  onChange={(e) => setPaypal(e.target.value)}
                  placeholder={t('createModal.placeholders.paypal')}
                  data-testid="contact-paypal-input"
                />
              </div>
            </div>

            <hr className="form-divider" />

            {/* --- Optionen --- */}
            <div className="form-section">
              <h3 className="form-section-title">{t('createModal.sections.options')}</h3>

              <div className="form-group form-group--inline">
                <label htmlFor="contact-monthly-report">{t('createModal.fields.monthlyReport')}</label>
                <input
                  id="contact-monthly-report"
                  type="checkbox"
                  checked={monthlyReport}
                  onChange={(e) => setMonthlyReport(e.target.checked)}
                  data-testid="contact-monthly-report-toggle"
                />
              </div>

              <div className="form-group form-group--inline">
                <label htmlFor="contact-broadcast-enabled">{t('createModal.fields.broadcastEnabled')}</label>
                <input
                  id="contact-broadcast-enabled"
                  type="checkbox"
                  checked={broadcastEnabled}
                  onChange={(e) => setBroadcastEnabled(e.target.checked)}
                  data-testid="contact-broadcast-enabled-toggle"
                />
              </div>

              <div className="form-group">
                <label htmlFor="contact-tag-list">{t('createModal.fields.tagList')}</label>
                <input
                  id="contact-tag-list"
                  className="input"
                  type="text"
                  value={tagList}
                  onChange={(e) => setTagList(e.target.value)}
                  placeholder={t('createModal.placeholders.tagList')}
                  data-testid="contact-tag-list-input"
                />
              </div>
            </div>

          </div>

          <div className="modal-footer" style={{ position: 'sticky', bottom: 0 }}>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleClose}
              disabled={createContact.isPending}
            >
              {t('createModal.cancelBtn')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={createContact.isPending}
              data-testid="create-contact-submit"
            >
              {createContact.isPending ? t('createModal.submitPending') : t('createModal.submitBtn')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
